import { useState } from "react";
import { Check, Copy, Heart, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QRCodeCanvas } from "@/components/ui/qrcode";
import { useToast } from "@/hooks/useToast";
import { DONATE_ADDRESS } from "@/lib/resources";
import { track } from "@/lib/track";

/** The address without the "bitcoincash:" URI scheme, for display. */
const DISPLAY_ADDRESS = DONATE_ADDRESS.replace(/^bitcoincash:/, "");

export function DonateCard() {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  async function copyAddress() {
    track("donate_click");
    try {
      await navigator.clipboard.writeText(DISPLAY_ADDRESS);
      setCopied(true);
      toast({
        title: "Address copied!",
        description: "Thank you for supporting BCH School. 💚",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: "Couldn't copy",
        description: "Please copy the address manually.",
        variant: "destructive",
      });
    }
  }

  return (
    <Card className="mx-auto mt-6 max-w-2xl overflow-hidden border-2 print:hidden">
      <CardContent className="p-6 sm:p-8">
        <div className="grid items-center gap-6 sm:grid-cols-[auto_1fr]">
          {/* QR code */}
          <div className="mx-auto rounded-2xl bg-white p-3 shadow-sm ring-1 ring-border">
            <QRCodeCanvas
              value={DONATE_ADDRESS}
              size={160}
              level="M"
              className="h-40 w-40 rounded-lg"
            />
          </div>

          {/* Copy + text */}
          <div className="space-y-4 text-center sm:text-left">
            <div>
              <div className="mb-1 inline-flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-sm">
                  <Heart className="h-4 w-4 fill-current" />
                </span>
                <h2 className="text-xl font-bold">Enjoyed BCH School?</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                BCH School is free and always will be. If it helped you, consider
                sending a small tip in real Bitcoin Cash — every sat keeps the
                lessons growing. Totally optional. 💚
              </p>
            </div>

            <div className="rounded-xl bg-secondary/60 p-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                BCH address
              </p>
              <p className="break-all font-mono text-sm text-foreground">
                {DISPLAY_ADDRESS}
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button onClick={copyAddress} className="sm:flex-1">
                {copied ? (
                  <Check className="mr-1.5 h-4 w-4" />
                ) : (
                  <Copy className="mr-1.5 h-4 w-4" />
                )}
                {copied ? "Copied!" : "Copy address"}
              </Button>
              <Button
                asChild
                variant="outline"
                className="sm:flex-1"
                onClick={() => track("donate_click")}
              >
                <a href={DONATE_ADDRESS}>
                  <Wallet className="mr-1.5 h-4 w-4" />
                  Open in wallet
                </a>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
